import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Star, Users, TrendingUp, Gift, RefreshCw, Search, Award, Download, CheckSquare, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLoyalty = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [awardForm, setAwardForm] = useState({ client_id: '', points: '', description: '' });
  const [showAwardForm, setShowAwardForm] = useState(null);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [bulkPoints, setBulkPoints] = useState('');
  const [bulkDesc, setBulkDesc] = useState('');
  const [bulkAwarding, setBulkAwarding] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = async () => {
    try {
      const [overviewRes, clientsRes] = await Promise.all([
        fetch(`${API}/api/loyalty/admin/overview`),
        fetch(`${API}/api/crm/clients`)
      ]);
      setOverview(await overviewRes.json());
      const clientsData = await clientsRes.json();
      setClients(clientsData.clients || clientsData || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleAwardPoints = async (clientId) => {
    const points = parseInt(awardForm.points);
    if (!points || points <= 0) {
      toast({ title: 'Invalid', description: 'Enter a positive number of points', variant: 'destructive' });
      return;
    }
    try {
      await fetch(`${API}/api/loyalty/admin/award`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, points, description: awardForm.description || 'Manual award by admin' })
      });
      toast({ title: 'Points Awarded', description: `${points} points awarded successfully` });
      setShowAwardForm(null);
      setAwardForm({ client_id: '', points: '', description: '' });
      loadData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const filteredClients = clients.filter(c =>
    !searchQuery || (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (c.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleBulkSelect = (clientId) => {
    setSelectedForBulk(prev => prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]);
  };

  const selectAll = () => {
    if (selectedForBulk.length === filteredClients.length) setSelectedForBulk([]);
    else setSelectedForBulk(filteredClients.map(c => c.client_id));
  };

  const handleBulkAward = async () => {
    const pts = parseInt(bulkPoints);
    if (!pts || pts <= 0 || selectedForBulk.length === 0) {
      toast({ title: 'Invalid', description: 'Select clients and enter points', variant: 'destructive' });
      return;
    }
    setBulkAwarding(true);
    try {
      const res = await fetch(`${API}/api/loyalty/admin/bulk-award`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: selectedForBulk, points: pts, description: bulkDesc || 'Bulk award' })
      });
      const data = await res.json();
      toast({ title: 'Bulk Award Complete', description: `${data.awarded_count} clients received ${pts} points` });
      setSelectedForBulk([]);
      setBulkPoints('');
      setBulkDesc('');
      loadData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    setBulkAwarding(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/loyalty/admin/export`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loyalty_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export downloaded' });
    } catch { toast({ title: 'Export failed', variant: 'destructive' }); }
    setExporting(false);
  };

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-loyalty">
      <header className="bg-black text-white py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Loyalty Management</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Points, rewards, and referrals</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} className="border-gray-700 text-gray-300 hover:text-white">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="border-gray-700 text-gray-300 hover:text-white" data-testid="export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {[
            { label: 'Points Issued', value: overview?.total_points_issued?.toLocaleString() || 0, icon: Star, bg: 'bg-yellow-100', color: 'text-yellow-600' },
            { label: 'Points Redeemed', value: overview?.total_points_redeemed?.toLocaleString() || 0, icon: Gift, bg: 'bg-red-100', color: 'text-red-600' },
            { label: 'Outstanding', value: overview?.points_outstanding?.toLocaleString() || 0, icon: TrendingUp, bg: 'bg-blue-100', color: 'text-blue-600' },
            { label: 'Referrals Done', value: overview?.total_referrals_completed || 0, icon: Users, bg: 'bg-green-100', color: 'text-green-600' },
            { label: 'Pending Refs', value: overview?.pending_referrals || 0, icon: Award, bg: 'bg-purple-100', color: 'text-purple-600' },
          ].map(({ label, value, icon: Icon, bg, color }, i) => (
            <Card key={i} className="p-4 border-gray-200" data-testid={`loyalty-stat-${i}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-2xl font-bold text-black">{value}</p>
                </div>
                <div className={`h-10 w-10 rounded-full ${bg} flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Top Earners */}
        {overview?.top_earners?.length > 0 && (
          <Card className="border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-black">Top Point Earners</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="top-earners-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rank</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Client</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Email</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600">Total Points</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {overview.top_earners.map((earner, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${i < 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-black">{earner.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{earner.email}</td>
                      <td className="py-3 px-4 text-sm text-right font-bold text-red-600">{earner.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Client List with Award */}
        <Card className="border-gray-200">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-bold text-black">Client Points Management</h3>
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search clients..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="client-search" />
              </div>
            </div>
          </div>

          {/* Bulk Award Bar */}
          {selectedForBulk.length > 0 && (
            <div className="bg-red-50 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 border-b border-red-200" data-testid="bulk-award-bar">
              <span className="text-sm font-semibold text-red-700">{selectedForBulk.length} selected</span>
              <Input type="number" placeholder="Points" value={bulkPoints} onChange={e => setBulkPoints(e.target.value)} className="h-8 w-24 text-sm" data-testid="bulk-points-input" />
              <Input placeholder="Reason (optional)" value={bulkDesc} onChange={e => setBulkDesc(e.target.value)} className="h-8 flex-1 text-sm" />
              <Button size="sm" onClick={handleBulkAward} disabled={bulkAwarding} className="bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="bulk-award-btn">
                {bulkAwarding ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Award className="h-3 w-3 mr-1" />} Bulk Award
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedForBulk([])} className="text-xs h-8">Clear</Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 px-4 w-10">
                    <button onClick={selectAll} className="text-gray-400 hover:text-black" data-testid="select-all-btn">
                      <CheckSquare className={`h-4 w-4 ${selectedForBulk.length === filteredClients.length && filteredClients.length > 0 ? 'text-red-600' : ''}`} />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Client</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Tier</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredClients.slice(0, 25).map((client) => (
                  <React.Fragment key={client.client_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input type="checkbox" checked={selectedForBulk.includes(client.client_id)}
                          onChange={() => toggleBulkSelect(client.client_id)}
                          className="h-4 w-4 text-red-600 rounded border-gray-300"
                          data-testid={`select-${client.client_id}`} />
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-black">{client.name || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{client.email || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm capitalize">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${client.membership_tier === 'penthouse' ? 'bg-yellow-100 text-yellow-800' : client.membership_tier === 'condo' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {client.membership_tier || 'lobby'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button size="sm" variant="outline" onClick={() => setShowAwardForm(showAwardForm === client.client_id ? null : client.client_id)}
                          className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white text-xs"
                          data-testid={`award-btn-${client.client_id}`}>
                          <Star className="h-3 w-3 mr-1" /> Award Points
                        </Button>
                      </td>
                    </tr>
                    {showAwardForm === client.client_id && (
                      <tr className="bg-red-50">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="flex flex-col sm:flex-row gap-2 items-end">
                            <div className="flex-1">
                              <label className="text-xs text-gray-600">Points</label>
                              <Input type="number" placeholder="e.g. 500" value={awardForm.points} onChange={e => setAwardForm({...awardForm, points: e.target.value})} className="h-8" data-testid={`points-input-${client.client_id}`} />
                            </div>
                            <div className="flex-[2]">
                              <label className="text-xs text-gray-600">Reason</label>
                              <Input placeholder="e.g. Loyalty bonus, Apology credit" value={awardForm.description} onChange={e => setAwardForm({...awardForm, description: e.target.value})} className="h-8" />
                            </div>
                            <Button size="sm" onClick={() => handleAwardPoints(client.client_id)} className="bg-red-600 hover:bg-red-700 text-xs h-8"
                              data-testid={`confirm-award-${client.client_id}`}>
                              Award
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filteredClients.length === 0 && <div className="p-8 text-center text-gray-500">No clients found</div>}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoyalty;
